/**
 * Taking a bill across several methods.
 *
 * The remaining amount is the largest thing here and updates as parts are
 * entered, because that is the number the cashier is working towards and the
 * one they would otherwise be doing in their head with a queue waiting.
 */
import { useState } from 'react';
import { formatPKR } from '@foodrun/shared';

import { paidSoFar, remaining, isSettled, toPayments } from './splitPayment.js';
import { Button } from '../../components/ui/Button.jsx';
import { ManagerApproval } from './ManagerApproval.jsx';
import { needsApproval } from './approvalCodes.js';

const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'online', label: 'Online' },
];

export function SplitPanel({ total, onConfirm, onCancel, busy, error }) {
  const [parts, setParts] = useState([{ method: 'cash', amount: '' }]);

  const left = remaining(total, parts);
  const settled = isSettled(total, parts);

  function update(index, patch) {
    setParts((current) => current.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  function addPart() {
    // Pre-filled with whatever is left, which is what the next part almost
    // always is.
    setParts((current) => [...current, { method: 'card', amount: left > 0 ? String(left) : '' }]);
  }

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h2 className="font-semibold text-lg text-ink">Split the bill</h2>
        <span className="text-lg font-semibold tabular-nums text-ink">{formatPKR(total)}</span>
      </div>

      <ul className="mt-4 space-y-2">
        {parts.map((part, index) => (
          <li key={index} className="flex items-center gap-2">
            <select
              aria-label={`Payment ${index + 1} method`}
              value={part.method}
              onChange={(e) => update(index, { method: e.target.value })}
              className="h-12 rounded-lg border border-rule bg-surface-3 px-3 text-[15px] focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            >
              {METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

            <input
              type="number"
              inputMode="decimal"
              min="0"
              aria-label={`Payment ${index + 1} amount`}
              value={part.amount}
              onChange={(e) => update(index, { amount: e.target.value })}
              placeholder="0"
              className="h-12 min-w-0 flex-1 rounded-lg border border-rule bg-surface-3 px-3.5 text-ink text-[15px] font-semibold tabular-nums focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />

            {parts.length > 1 && (
              <button
                onClick={() => setParts((c) => c.filter((_, i) => i !== index))}
                aria-label={`Remove payment ${index + 1}`}
                className="size-12 shrink-0 rounded-lg text-ink-2 hover:bg-surface-3"
              >
                ×
              </button>
            )}
          </li>
        ))}
      </ul>

      <Button variant="secondary" className="mt-2 w-full" onClick={addPart} disabled={busy}>
        Add another payment
      </Button>

      <div
        aria-live="polite"
        className={`mt-4 flex items-baseline justify-between rounded-lg px-3.5 py-3 ${
          settled ? 'bg-good-bg' : left < 0 ? 'bg-bad-bg' : 'bg-warn-bg'
        }`}
      >
        <span
          className={`text-sm font-medium ${
            settled ? 'text-good' : left < 0 ? 'text-bad' : 'text-warn'
          }`}
        >
          {settled ? 'Fully paid' : left < 0 ? 'Over by' : 'Still to pay'}
        </span>
        <span
          className={`text-2xl font-semibold tabular-nums ${
            settled ? 'text-good' : left < 0 ? 'text-bad' : 'text-warn'
          }`}
        >
          {settled ? formatPKR(paidSoFar(parts)) : formatPKR(Math.abs(left))}
        </span>
      </div>

      {/*
        The same refusal reaches here, and without this it is a dead end: the
        message says a manager has to approve it and there is nowhere to put a
        PIN. A split bill is exactly when a discount is most likely — a table
        of six with one complaint — so it is not a rare corner.
      */}
      {needsApproval(error) ? (
        <ManagerApproval
          error={error}
          busy={busy}
          onApprove={(approval) => onConfirm({ payments: toPayments(parts), approval })}
        />
      ) : (
        error && (
          <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
            {error.message}
          </p>
        )
      )}

      <div className="mt-5 flex gap-2">
        <Button variant="secondary" size="lg" onClick={onCancel} disabled={busy}>
          Back
        </Button>
        <Button
          size="lg"
          className="flex-1"
          busy={busy}
          // A split that does not add up records the order as underpaid: it
          // never settles, and the shift will not reconcile.
          disabled={!settled}
          onClick={() => onConfirm({ payments: toPayments(parts) })}
        >
          Complete order
        </Button>
      </div>
    </div>
  );
}
